import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheck, faExclamationTriangle, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, Timestamp, serverTimestamp } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { UserContext } from '../../context/UserContext';
import queryString from 'query-string';
import styles from './TourChecklistQRHandler.module.css';

// This component handles QR code scans for tour checklist items
const TourChecklistQRHandler = () => {
  const { itemId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  
  const [isLoading, setIsLoading] = useState(true);
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');
  
  // Parse query parameters
  const queryParams = queryString.parse(location.search);
  const scanMode = queryParams.mode || 'view'; // Default to view mode
  const tourId = queryParams.tourId;
  const dateId = queryParams.dateId;
  const checklistMode = queryParams.checklistMode || 'checkIn'; // checkIn or checkOut
  
  useEffect(() => {
    if (!userData?.userId) {
      // Save the current URL for redirect after login
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
      navigate('/login');
      return;
    }
    
    const handleScan = async () => {
      try {
        setIsLoading(true);
        
        // Parse item ID to get checklist item ID and index
        let checklistItemId, itemIndex;
        
        if (itemId.includes('_')) {
          const parts = itemId.split('_');
          if (parts.length >= 2) {
            // Last part is the index, everything before is the checklist item ID
            itemIndex = parseInt(parts[parts.length - 1]);
            checklistItemId = parts.slice(0, -1).join('_');
          }
        }
        
        if (!checklistItemId || isNaN(itemIndex)) {
          setError('Invalid QR code format. Please check the QR code and try again.');
          setIsLoading(false);
          return;
        }
        
        const db = getFirestoreInstance();
        
        // If tourId and dateId are provided in query params, use them
        if (tourId && dateId) {
          const checklistItemRef = doc(
            db,
            'Tours',
            tourId,
            'tourDates',
            dateId,
            'tourChecklist',
            checklistItemId
          );
          
          const checklistItemDoc = await getDoc(checklistItemRef);
          
          if (!checklistItemDoc.exists()) {
            setError('Checklist item not found. Please check the QR code and try again.');
            setIsLoading(false);
            return;
          }
          
          const checklistItemData = checklistItemDoc.data();
          
          // Check if user has permission to check items
          // First check if user is the creator
          const tourRef = doc(db, 'Tours', tourId);
          const tourDoc = await getDoc(tourRef);
          
          if (!tourDoc.exists()) {
            setError('Tour not found.');
            setIsLoading(false);
            return;
          }
          
          const tourData = tourDoc.data();
          const isCreator = userData.userId === tourData.createdBy?.userId;
          
          let hasAccess = isCreator;
          
          // If not creator, check allowed users in metadata
          if (!hasAccess) {
            try {
              const metadataRef = doc(
                db,
                'Tours',
                tourId,
                'tourDates',
                dateId,
                'checklistMetadata',
                'access'
              );
              
              const metadataDoc = await getDoc(metadataRef);
              
              if (metadataDoc.exists()) {
                const allowedUsers = metadataDoc.data().allowedUsers || [];
                hasAccess = allowedUsers.some(user => user.userId === userData.userId);
              }
            } catch (metadataError) {
              console.error('Error checking access permissions:', metadataError);
            }
          }
          
          if (!hasAccess) {
            setError("You don't have permission to check items in this checklist. Contact the tour creator for access.");
            setIsLoading(false);
            return;
          }
          
          // Check if the specific item index is valid
          if (itemIndex < 0 || itemIndex >= checklistItemData.quantity) {
            setError('Invalid item index in QR code.');
            setIsLoading(false);
            return;
          }
          
          // Handle different scan modes
          switch (scanMode) {
            case 'check':
              try {
                // Get current status arrays
                const currentCheckInStatus = checklistItemData.checkInStatus || [];
                const currentCheckOutStatus = checklistItemData.checkOutStatus || [];
                
                // Ensure arrays are large enough
                while (currentCheckInStatus.length < checklistItemData.quantity) {
                  currentCheckInStatus.push({
                    isCheckedIn: false,
                    checkedInBy: null,
                    checkedInByName: null,
                    checkedInAt: null
                  });
                }
                
                while (currentCheckOutStatus.length < checklistItemData.quantity) {
                  currentCheckOutStatus.push({
                    isCheckedOut: false,
                    checkedOutBy: null,
                    checkedOutByName: null,
                    checkedOutAt: null
                  });
                }
                
                // Check if item is already checked
                const isAlreadyChecked = checklistMode === 'checkIn' 
                  ? currentCheckInStatus[itemIndex]?.isCheckedIn
                  : currentCheckOutStatus[itemIndex]?.isCheckedOut;
                
                if (isAlreadyChecked) {
                  setError(`This item is already ${checklistMode === 'checkIn' ? 'checked in' : 'checked out'}.`);
                  break;
                }
                
                // Update the status
                const timestamp = Timestamp.now();
                
                if (checklistMode === 'checkIn') {
                  currentCheckInStatus[itemIndex] = {
                    isCheckedIn: true,
                    checkedInBy: userData.userId,
                    checkedInByName: userData.fullName || 'Unknown User',
                    checkedInAt: timestamp
                  };
                  
                  await updateDoc(checklistItemRef, {
                    checkInStatus: currentCheckInStatus,
                    updatedAt: serverTimestamp(),
                    updatedBy: userData.userId
                  });
                } else {
                  currentCheckOutStatus[itemIndex] = {
                    isCheckedOut: true,
                    checkedOutBy: userData.userId,
                    checkedOutByName: userData.fullName || 'Unknown User',
                    checkedOutAt: timestamp
                  };
                  
                  await updateDoc(checklistItemRef, {
                    checkOutStatus: currentCheckOutStatus,
                    updatedAt: serverTimestamp(),
                    updatedBy: userData.userId
                  });
                }
                
                setSuccess(true);
                setMessage(`${checklistItemData.name} (Item ${itemIndex + 1}) successfully ${checklistMode === 'checkIn' ? 'checked in' : 'checked out'}.`);
                
                // Automatically go back after a short delay
                setTimeout(() => {
                  navigate(`/tour/${tourId}/${dateId}/checklist`);
                }, 2000);
              } catch (err) {
                console.error('Error updating checklist:', err);
                setError('Failed to update checklist: ' + err.message);
              }
              break;
              
            case 'view':
            default:
              // Navigate to the checklist page with this item highlighted
              navigate(`/tour/${tourId}/${dateId}/checklist`, {
                state: { 
                  highlightItem: checklistItemId,
                  highlightIndex: itemIndex
                }
              });
              return;
          }
        } else {
          // If no tour/date provided, try to find the item across all tours
          // This is more complex and would require searching multiple collections
          setError('Missing tour or date information. Please scan from within a specific tour checklist.');
        }
      } catch (error) {
        console.error('Error handling QR scan:', error);
        setError('An error occurred while processing the scan.');
      } finally {
        setIsLoading(false);
      }
    };
    
    handleScan();
  }, [itemId, userData, navigate, scanMode, tourId, dateId, checklistMode]);
  
  // Content to display based on state
  if (isLoading) {
    return (
      <div className={styles.scanContainer}>
        <div className={styles.loadingContainer}>
          <FontAwesomeIcon icon={faSpinner} spin className={styles.spinnerIcon} />
          <p>Processing checklist QR code...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={styles.scanContainer}>
        <div className={styles.errorContainer}>
          <FontAwesomeIcon icon={faExclamationTriangle} className={styles.errorIcon} />
          <h3>Scan Error</h3>
          <p>{error}</p>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} className={styles.buttonIcon} />
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className={styles.scanContainer}>
        <div className={styles.successContainer}>
          <FontAwesomeIcon icon={faCheck} className={styles.successIcon} />
          <h3>Success!</h3>
          <p>{message}</p>
          <p className={styles.redirectMessage}>Redirecting to checklist...</p>
        </div>
      </div>
    );
  }
  
  // If we reached here, view mode is active and we're redirecting
  return (
    <div className={styles.scanContainer}>
      <div className={styles.loadingContainer}>
        <FontAwesomeIcon icon={faSpinner} spin className={styles.spinnerIcon} />
        <p>Redirecting to checklist...</p>
      </div>
    </div>
  );
};

export default TourChecklistQRHandler;